import api from './api'

/**
 * Push do navegador — inscrição e estado (painel dos atendentes).
 *
 * Port do `frontend/src/services/webPush.ts` do dashboard: o navegador gera
 * uma inscrição com a chave pública (VAPID), a API do suporte guarda na tabela
 * compartilhada com `app = 'support'` e usa para mandar push, que o /sw.js
 * deste painel mostra como notificação do sistema mesmo com a aba fechada.
 *
 * Firefox e Safari só deixam pedir permissão a partir de um clique, e o
 * Chrome esconde o pedido quando ele aparece sozinho. Por isso `ativarPush`
 * é sempre chamado de um botão.
 */

export type EstadoPush =
  | 'indisponivel' // navegador sem suporte
  | 'precisa-instalar' // iPhone/iPad fora da tela inicial
  | 'desligado-servidor' // API sem chaves VAPID
  | 'pendente' // ainda não perguntamos (ou o atendente fechou sem responder)
  | 'bloqueado' // negou — só ele libera nas configurações do site
  | 'ativo'

const temSuporte = () =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window

export const ehIOS = () =>
  /iphone|ipad|ipod/i.test(navigator.userAgent) ||
  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)

export const ehAppInstalado = () =>
  window.matchMedia?.('(display-mode: standalone)').matches ||
  (navigator as any).standalone === true

let chave: Promise<{ enabled: boolean; publicKey: string | null }> | null = null

const buscarChave = () => {
  chave ??= api
    .get('/push/public-key')
    .then(({ data }) => ({
      enabled: !!data?.enabled,
      publicKey: data?.publicKey || null,
    }))
    .catch(() => {
      chave = null
      return { enabled: false, publicKey: null }
    })
  return chave
}

function base64UrlParaBytes(base64: string) {
  const preenchido = (base64 + '='.repeat((4 - (base64.length % 4)) % 4))
    .replace(/-/g, '+')
    .replace(/_/g, '/')
  const bruto = atob(preenchido)
  return Uint8Array.from(bruto, (c) => c.charCodeAt(0))
}

function mesmaChave(atual: ArrayBuffer | null | undefined, publica: string) {
  if (!atual) return false
  const a = new Uint8Array(atual)
  const b = base64UrlParaBytes(publica)
  return a.length === b.length && a.every((byte, i) => byte === b[i])
}

export async function estadoDoPush(): Promise<EstadoPush> {
  if (!temSuporte()) {
    return ehIOS() && !ehAppInstalado() ? 'precisa-instalar' : 'indisponivel'
  }
  const { enabled } = await buscarChave()
  if (!enabled) return 'desligado-servidor'
  if (Notification.permission === 'denied') return 'bloqueado'
  if (Notification.permission === 'default') return 'pendente'
  return 'ativo'
}

/**
 * Garante que este navegador está inscrito e que a API sabe disso. Chamado a
 * cada abertura com permissão concedida: a inscrição pode ter sido apagada
 * por falha, pela chave ter mudado ou por outra conta ter entrado.
 */
export async function sincronizarInscricao(): Promise<boolean> {
  if (!temSuporte() || Notification.permission !== 'granted') return false
  const { enabled, publicKey } = await buscarChave()
  if (!enabled || !publicKey) return false

  const registro = await navigator.serviceWorker.register('/sw.js', {
    scope: '/',
  })
  await navigator.serviceWorker.ready

  let inscricao = await registro.pushManager.getSubscription()
  if (
    inscricao &&
    !mesmaChave(inscricao.options?.applicationServerKey, publicKey)
  ) {
    await inscricao.unsubscribe().catch(() => undefined)
    inscricao = null
  }
  inscricao ??= await registro.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: base64UrlParaBytes(publicKey),
  })

  await api.post('/push/subscribe', { subscription: inscricao.toJSON() })
  return true
}

/** Pede permissão (precisa vir de um clique) e inscreve. */
export async function ativarPush(): Promise<EstadoPush> {
  if (!temSuporte()) return estadoDoPush()
  const permissao = await Notification.requestPermission()
  if (permissao === 'denied') return 'bloqueado'
  if (permissao !== 'granted') return 'pendente'
  await sincronizarInscricao()
  return 'ativo'
}

/**
 * No logout: tira a inscrição da API e do navegador. Não segura o logout por
 * mais de 1,5s.
 */
export async function desinscreverNoLogout() {
  try {
    if (!('serviceWorker' in navigator)) return
    const registro = await navigator.serviceWorker.getRegistration('/')
    const inscricao = await registro?.pushManager.getSubscription()
    if (!inscricao) return
    await Promise.race([
      api.post('/push/unsubscribe', { endpoint: inscricao.endpoint }),
      new Promise((resolve) => setTimeout(resolve, 1500)),
    ])
    await inscricao.unsubscribe()
  } catch {
    /* logout segue de qualquer jeito */
  }
}
